import React, { useEffect, useMemo, useState } from "react";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { BanknotesIcon } from "@heroicons/react/20/solid";
import { storage } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { showAlert } from "../../alertService";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import { resolucionDeposito } from "../../utils/depositoEstado";
import MetodoPagoField from "./MetodoPagoField";
import DocumentUploader from "./DocumentUploader";
import DialogoConfirmacion, { Resumen, Fila } from "./DialogoConfirmacion";

// Resolver un depósito en garantía: un solo diálogo para aplicar, devolver o
// repartir. El operador dice cuánto se queda el venue (chips de porcentaje,
// porcentaje libre o monto en pesos) y ve el reparto antes de guardar; lo
// que no se aplica se devuelve. Lo usan los depósitos del cliente (evento) y
// los del proveedor (ficha): cada padre traduce el resultado a su doc con
// resolucionDeposito() y guarda el comprobante a su manera.
//
// onSubmit({ montoAplicado, montoDevuelto, fechaStr, metodo, notas, archivo })
// `archivo` es el payload crudo de DocumentUploader (o null) y solo viene
// cuando hay devolución.

const fmtMoney = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "$0";
  return "$" + Math.abs(n).toLocaleString("es-MX", {
    minimumFractionDigits: n % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const hoyInput = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const PCTS = [0, 25, 50, 75, 100];

const inputClass =
  "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

const ResolverDepositoDialog = ({
  open,
  onClose,
  deposito,
  fuente = "cliente", // "cliente" | "proveedor" — solo cambia el texto
  storagePathPrefix = null,
  saving = false,
  onSubmit,
}) => {
  const { metodosPagoConDefaults: metodosPago } = useVenue();
  const total = Number(deposito?.monto) || 0;
  const contraparte = fuente === "proveedor" ? "al proveedor" : "al cliente";

  const [aplicadoStr, setAplicadoStr] = useState("0");
  const [pctStr, setPctStr] = useState("0");
  const [fechaStr, setFechaStr] = useState(hoyInput());
  const [metodo, setMetodo] = useState("");
  const [notas, setNotas] = useState("");
  const [pendingArchivo, setPendingArchivo] = useState(null);

  // Cada apertura arranca limpia: devolver todo es lo más común, y aplicar
  // todo está a un clic en el chip de 100%.
  useEffect(() => {
    if (!open) return;
    setAplicadoStr("0");
    setPctStr("0");
    setFechaStr(hoyInput());
    setMetodo("");
    setNotas("");
    setPendingArchivo(null);
  }, [open, deposito?.id]);

  const aplicadoNum = Number(aplicadoStr) || 0;
  const fueraDeRango = aplicadoNum < 0 || aplicadoNum > total;
  const resolucion = useMemo(() => resolucionDeposito(total, aplicadoNum), [total, aplicadoNum]);
  const devuelto = resolucion.montoDevuelto == null ? 0 : resolucion.montoDevuelto;
  const aplicado = resolucion.montoAplicado;

  // Monto que corresponde a un porcentaje: 100% es el total EXACTO (con sus
  // centavos) y lo demás se redondea a centavos y se acota al total; redondear
  // a pesos dejaba el 100% fuera de rango con $5,000.50 y convertía
  // $1,000.25 en una "devolución parcial" de 25 centavos.
  const montoDePorcentaje = (p) =>
    p >= 100 ? total : Math.min(total, Math.round(total * p) / 100);
  const setPorPorcentaje = (pct) => {
    const p = Math.min(100, Math.max(0, Number(pct) || 0));
    setPctStr(String(p));
    setAplicadoStr(String(montoDePorcentaje(p)));
  };
  const setPorMonto = (raw) => {
    const limpio = normalizeMoneyInput(raw);
    setAplicadoStr(limpio);
    const n = Number(limpio) || 0;
    setPctStr(total > 0 ? String(Math.round((Math.min(total, Math.max(0, n)) / total) * 100)) : "0");
  };
  const pctActivo = Number(pctStr);

  const cleanupPendingBlob = async () => {
    if (pendingArchivo?.storagePath) {
      try { await deleteObject(storageRef(storage, pendingArchivo.storagePath)); } catch (_) {}
    }
  };
  const handleAttachUploaded = async (payload) => {
    await cleanupPendingBlob();
    setPendingArchivo(payload);
  };
  const handleRemoveArchivo = async () => {
    await cleanupPendingBlob();
    setPendingArchivo(null);
  };
  const handleClose = async () => {
    if (saving) return;
    await cleanupPendingBlob();
    setPendingArchivo(null);
    onClose?.();
  };

  const handleConfirm = async () => {
    if (fueraDeRango) {
      showAlert(`Lo aplicado debe estar entre $0 y ${fmtMoney(total)}.`, "error");
      return;
    }
    if (!fechaStr) {
      showAlert("Indica la fecha.", "error");
      return;
    }
    let archivo = pendingArchivo;
    if (devuelto <= 0 && archivo) {
      // Sin devolución no hay a qué colgar el comprobante: se suelta el blob.
      await cleanupPendingBlob();
      archivo = null;
      setPendingArchivo(null);
    }
    await onSubmit?.({
      montoAplicado: aplicado,
      montoDevuelto: devuelto,
      fechaStr,
      metodo: devuelto > 0 ? metodo || null : null,
      notas: notas.trim(),
      archivo,
    });
  };

  const labelConfirmar =
    devuelto <= 0 ? "Aplicar depósito" : aplicado <= 0 ? "Registrar devolución" : "Aplicar y devolver";

  const tituloFecha =
    devuelto <= 0 ? "Fecha de aplicación" : aplicado <= 0 ? "Fecha de devolución" : "Fecha";

  return (
    <DialogoConfirmacion
      open={open}
      onClose={handleClose}
      titulo="Resolver depósito"
      subtitulo={`${fmtMoney(total)} en custodia${deposito?.fecha?.toDate ? ` desde el ${deposito.fecha.toDate().toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}` : ""}`}
      icono={BanknotesIcon}
      saving={saving}
      cancelarLabel="Cancelar"
      confirmar={{
        label: labelConfirmar,
        savingLabel: "Guardando…",
        onClick: handleConfirm,
        disabled: fueraDeRango || !fechaStr || total <= 0,
      }}
    >
      <div>
        <div className="text-xs font-medium text-zinc-500 mb-2">
          ¿Cuánto se queda el venue?
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {PCTS.map((pct) => {
            const activo = pctActivo === pct && String(montoDePorcentaje(pct)) === aplicadoStr;
            return (
              <button
                key={pct}
                type="button"
                onClick={() => setPorPorcentaje(pct)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                  activo
                    ? "border-transparent text-white"
                    : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                }`}
                style={activo ? { backgroundColor: "#1b4d2e" } : undefined}
              >
                {pct}%
              </button>
            );
          })}
          <div className="relative">
            <input
              type="text"
              inputMode="numeric"
              value={pctStr}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d]/g, "");
                if (!raw) { setPctStr(""); setAplicadoStr("0"); return; }
                setPorPorcentaje(raw);
              }}
              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              className="w-16 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-2 pr-6 py-1.5 text-xs text-center focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Monto que se aplica</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
            <input
              type="text"
              inputMode="decimal"
              pattern="-?[0-9.,]*"
              value={aplicadoStr ? fmtMXNInput(aplicadoStr) : ""}
              onChange={(e) => setPorMonto(e.target.value)}
              placeholder="0"
              className={inputClass + " pl-7"}
            />
          </div>
          {fueraDeRango && (
            <div className="text-xs text-red-600 mt-1">
              Debe estar entre $0 y {fmtMoney(total)}.
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">{tituloFecha}</label>
          <input
            type="date"
            value={fechaStr}
            onChange={(e) => setFechaStr(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <Resumen titulo="Qué pasa con el depósito">
        <Fila tono={aplicado > 0 ? "rojo" : "zinc"} monto={fmtMoney(aplicado)}>
          Se aplica
          <span className="text-zinc-500"> — el venue se lo queda y cuenta como ingreso</span>
        </Fila>
        <Fila tono={devuelto > 0 ? "verde" : "zinc"} monto={fmtMoney(devuelto)}>
          Se devuelve {contraparte}
        </Fila>
      </Resumen>

      {devuelto > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Método de devolución</label>
            <MetodoPagoField
              value={metodo}
              onChange={setMetodo}
              metodosPago={metodosPago}
              className={inputClass}
            />
          </div>
          {storagePathPrefix && (
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Comprobante de devolución (opcional)
              </label>
              {pendingArchivo ? (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-zinc-800 dark:text-zinc-200">{pendingArchivo.filename}</div>
                    <div className="text-xs text-zinc-400">Adjunto al guardar</div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveArchivo}
                    className="text-xs text-zinc-500 hover:text-red-600"
                  >
                    Quitar
                  </button>
                </div>
              ) : (
                <DocumentUploader
                  storagePathPrefix={storagePathPrefix}
                  buttonLabel="Adjuntar comprobante"
                  withDownloadUrl={false}
                  onUploaded={handleAttachUploaded}
                />
              )}
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1">Notas (opcional)</label>
        <textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          rows={2}
          placeholder={aplicado > 0 ? "Motivo de la aplicación: daños, penalización, no-show…" : "Detalle de la devolución…"}
          className={inputClass}
        />
      </div>
    </DialogoConfirmacion>
  );
};

export default ResolverDepositoDialog;
